import { HttpBackend, HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TokenDecoded, WsCliente, WsContacto, IWsCliente } from '@ciptech/classes';
import { Observable, of } from 'rxjs';
import { map, finalize, catchError } from 'rxjs/operators';
import { ApiResponse, MailRequestDto, ResetPasswordDto, SolicitudRegistroDto, SunatDniData, SunatRucData, UsuarioContClienteDto, ClienteDto } from '../dto';
import { IdempotencyService } from './ws-idempotency.service';
import { normalizeHttpError, handleApiError } from '../utils';

@Injectable()
export class WsAuthService {
	private http: HttpClient;
	endpoint = '/api';
	cContactosEndpoint = '/api/WsContactos';

	constructor(private handler: HttpBackend, private idempotencyService: IdempotencyService) {
		// Estas consultas http no pasarán por el HttpInterceptor
		this.http = new HttpClient(this.handler);
	}

	signIn(body: { username: string, password: string }): Observable<TokenDecoded> {
		const userData = `username=${encodeURIComponent(body.username)}` + `&password=${encodeURIComponent(body.password)}` + `&grant_type=password`;

		const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

		return this.http.post<TokenDecoded>(`/token`, userData, { headers }).pipe(
			map((res: TokenDecoded) => res)
		);
	}

	refreshToken(refreshToken: string): Observable<TokenDecoded> {
		const userData = `refresh_token=${refreshToken}&grant_type=refresh_token`;
		const headers = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' });

		return this.http.post<TokenDecoded>(`/token`, userData, { headers }).pipe(
			map((res: TokenDecoded) => res)
		);
	}

	signUp(contacto: UsuarioContClienteDto, cliente?: ClienteDto): Observable<ApiResponse<null>> {
		const body: SolicitudRegistroDto = {
			Contacto: contacto,
			Cliente: cliente
		}
		return this.http.post<ApiResponse<null>>(`${this.cContactosEndpoint}/request-register`, body, { headers: { context: '.tab-content' } }).pipe(
			catchError(handleApiError<null>())
		);
	}

	verifyRegisterToken(token: string, payload: SolicitudRegistroDto | null = null): Observable<ApiResponse<number>> {
		return this.http.post<ApiResponse<number>>(`${this.cContactosEndpoint}/verify-register/${token}`, payload).pipe(
			catchError(handleApiError<number>())
		);
	}

	validateRegisterToken(token: string): Observable<ApiResponse<SolicitudRegistroDto>> {
		return this.http.get<ApiResponse<SolicitudRegistroDto>>(`${this.cContactosEndpoint}/validate-register/${token}`).pipe(
			catchError(handleApiError<SolicitudRegistroDto>())
		);
	}

	recovery(email: string): Observable<ApiResponse<null>> {
		const endpointName = 'request-password-reset';
		const body: MailRequestDto = { cEmail: email };
		// const key = this.idempotencyService.generateKey(endpointName);
		const headers = this.idempotencyService.createHeaders(endpointName);

		return this.http.post<ApiResponse<null>>(
			`${this.cContactosEndpoint}/${endpointName}`,
			body,
			{ headers: headers }
		).pipe(
			catchError(handleApiError<null>()),

			finalize(this.idempotencyService.finalizeClear(endpointName))
		);
	}

	resetPassword(cToken: string, password: string, passwordConf: string): Observable<ApiResponse<null>> {
		const endpointName = 'verify-password-reset';
		const headers = this.idempotencyService.createHeaders(endpointName);
		const body: ResetPasswordDto = {
			cPassword: password,
			cPasswordConf: passwordConf
		};
		return this.http.post<ApiResponse<null>>(`${this.cContactosEndpoint}/${endpointName}/${cToken}`, body, { headers: headers }).pipe(
			catchError(handleApiError<null>()),
			finalize(this.idempotencyService.finalizeClear(endpointName))
		);
	}

	getDataByRuc(cRuc: string): Observable<ApiResponse<SunatRucData>> {
		return this.http.get<ApiResponse<SunatRucData>>(`${this.endpoint}/Services/consulta-ruc/${cRuc}`).pipe(
			catchError(handleApiError<SunatRucData>())
		);
	}

	getDataByDni(cDni: string): Observable<ApiResponse<SunatDniData>> {
		return this.http.get<ApiResponse<SunatDniData>>(`${this.endpoint}/Services/consulta-dni/${cDni}`).pipe(
			catchError(handleApiError<SunatDniData>())
		);
	}
}
